"use client";

import { useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { AnimatePresence, motion } from "framer-motion";
import { type StaticImageData } from "next/image";
import { RENTAL_VEHICLES } from "@/data/vehicles";
import { AnimatedList } from "@/components/magicui/animated-list";
import { Icon } from "@/components/ui/Icon";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { LANDING, type LandingData } from "./landingDict";
import {
  cardBackdropByTheme,
  VehicleAutoSliderBackground,
  PaymentMethodsBackground,
  SupportIconBackground,
  SecurityTrustBackground,
} from "./valuePropsBackdrops";

// MagicBento dépend de GSAP (animations) : chargé dynamiquement côté client
// uniquement (ssr:false) pour alléger le first-load JS. Un placeholder réserve
// la hauteur afin d'éviter le décalage de mise en page (CLS).
const MagicBento = dynamic(() => import("@/components/react-bits/MagicBento"), {
  ssr: false,
  loading: () => <div className="min-h-[600px] w-full" aria-hidden="true" />,
});

type ValuePropsCopy = LandingData["valueProps"];

function ExperienceReviewsList({ copy }: { copy: ValuePropsCopy }) {
  const reviews = copy.reviews;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % reviews.length);
    }, 4600);

    return () => window.clearInterval(timer);
  }, [reviews.length]);

  return (
    <div className="relative">
      <div className="hidden space-y-2 lg:block">
        <p className="text-[11px] uppercase tracking-[0.12em] text-white/60">
          {copy.reviewsEyebrow}
        </p>
        <div className="border-white/12 relative h-[156px] rounded-xl border bg-black/35 p-3">
          <div className="mb-2 h-1 w-full overflow-hidden rounded-full bg-white/10">
            <motion.div
              key={activeIndex}
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: 4.4, ease: "linear" }}
              className="h-full rounded-full bg-gradient-to-r from-primary/75 via-white/80 to-primary/75"
            />
          </div>
          <div className="relative h-[90px] overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                initial={false}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -10, filter: "blur(2px)" }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0"
              >
                <p className="text-white/92 text-sm leading-6 md:leading-7">
                  &ldquo;{reviews[activeIndex].quote}&rdquo;
                </p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.1em] text-white/60">
                  {reviews[activeIndex].source}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="mt-2 flex flex-nowrap gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {reviews.map((review, index) => (
              <span
                key={review.tag}
                className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-[0.12em] ${
                  activeIndex === index
                    ? "border-primary/55 bg-primary/20 text-primary"
                    : "border-white/20 bg-black/40 text-white/70"
                }`}
              >
                {review.tag}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="border-white/12 rounded-xl border bg-black/35 p-3 lg:hidden">
        <p className="text-[11px] uppercase tracking-[0.12em] text-white/60">
          {copy.googleEyebrow}
        </p>
        <div className="mt-2 flex items-center gap-1.5 text-primary">
          {Array.from({ length: 5 }).map((_, index) => (
            <Icon
              key={`star-${index}`}
              icon="mdi:star"
              width={16}
              height={16}
            />
          ))}
          <span className="ms-1 text-sm font-medium text-white/90">5.0</span>
        </div>
        <p className="mt-2 hidden text-xs text-white/80 sm:block">
          {copy.googleNote}
        </p>
      </div>
    </div>
  );
}

type ValuePropsSectionProps = {
  locale?: Locale;
};

export const ValuePropsSection = ({
  locale = "fr",
}: ValuePropsSectionProps) => {
  const copy = tr(LANDING, locale).valueProps;
  const allVehicleImages = useMemo(() => {
    const unique = new Map<string, StaticImageData>();
    for (const vehicle of RENTAL_VEHICLES) {
      const sources = [vehicle.images.main, ...vehicle.images.gallery];
      for (const img of sources) {
        unique.set(img.src, img);
      }
    }
    return Array.from(unique.values());
  }, []);

  const vehicleAnimatedListItems = useMemo(
    () =>
      RENTAL_VEHICLES.map((vehicle) => {
        const upperBrand = vehicle.brand.toUpperCase();
        const model = vehicle.name.toUpperCase().startsWith(upperBrand)
          ? vehicle.name.slice(vehicle.brand.length).trim()
          : vehicle.name;
        return `${vehicle.brand} — ${model || vehicle.name}`;
      }),
    [],
  );

  return (
    <section className="relative w-full overflow-hidden bg-white py-28 text-black">
      {/* Décor de fond */}
      {/* <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-primary/20 blur-[140px]" />
        <div className="absolute -bottom-40 right-1/3 h-[360px] w-[360px] rounded-full bg-white/10 blur-[120px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.06),transparent_60%)]" />
      </div> */}

      <div className="relative mx-auto max-w-7xl px-4">
        {/* En‑tête */}
        <div className="mb-14 text-center">
          <motion.p
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-15%" }}
            className="mb-4 text-sm uppercase tracking-[0.35em] text-black/60"
          >
            {copy.eyebrow}
          </motion.p>
          <motion.h2
            initial={false}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            viewport={{ once: true, margin: "-10%" }}
            className="font-space-age text-3xl tracking-[0.25em] md:text-4xl"
          >
            {copy.heading}
          </motion.h2>
          <p className="mx-auto mt-4 max-w-2xl text-black/70">{copy.intro}</p>
        </div>

        {/* Magic Bento */}
        <div className="flex justify-center">
          <MagicBento
            textAutoHide
            enableStars={false}
            enableSpotlight={false}
            enableBorderGlow
            enableTilt={false}
            enableMagnetism={false}
            clickEffect={false}
            spotlightRadius={500}
            particleCount={14}
            glowColor="196, 153, 45" /* proche de primary (#C4992D) */
            containerMaxWidthClass="max-w-[80rem]"
            cards={[
              {
                color: "#090909",
                title: copy.cards[0].title,
                description: copy.cards[0].description,
                label: copy.cards[0].label,
                backgroundMedia: (
                  <>
                    {cardBackdropByTheme("payment")}
                    <PaymentMethodsBackground />
                  </>
                ),
              },
              {
                color: "#101010",
                title: copy.cards[1].title,
                description: copy.cards[1].description,
                label: copy.cards[1].label,
                backgroundMedia: (
                  <>
                    {cardBackdropByTheme("support")}
                    <SupportIconBackground />
                  </>
                ),
              },
              {
                color: "#0b0b0b",
                title: copy.cards[2].title,
                description: copy.cards[2].description,
                label: copy.cards[2].label,
                className: "min-h-[270px] md:min-h-[340px]",
                media: <ExperienceReviewsList copy={copy} />,
                backgroundMedia: cardBackdropByTheme("experience"),
              },
              {
                color: "rgba(10,10,10,0.52)",
                title: copy.cards[3].title,
                description: copy.cards[3].description,
                label: copy.cards[3].label,
                className: "min-h-[250px] md:min-h-[300px]",
                media: (
                  <>
                    <AnimatedList
                      items={vehicleAnimatedListItems}
                      visibleItems={4}
                      itemHeight={30}
                      duration={42}
                      className="hidden h-[120px] bg-black/45 lg:block"
                      itemClassName="text-[11px] tracking-[0.08em]"
                    />
                    <div className="rounded-xl border border-white/15 bg-black/45 px-3 py-2 lg:hidden">
                      <p className="text-[10px] uppercase tracking-[0.12em] text-white/70">
                        {copy.catalogueLabel}
                      </p>
                      <p className="mt-1 text-sm text-white/90">
                        {copy.modelsAvailable(RENTAL_VEHICLES.length)}
                      </p>
                    </div>
                  </>
                ),
                backgroundMedia: (
                  <VehicleAutoSliderBackground
                    images={allVehicleImages}
                    alt={copy.fleetAlt}
                  />
                ),
              },
              {
                color: "#111111",
                title: copy.cards[4].title,
                description: copy.cards[4].description,
                label: copy.cards[4].label,
                backgroundMedia: cardBackdropByTheme("events", copy.eventsAlt),
              },
              {
                color: "#080808",
                title: copy.cards[5].title,
                description: copy.cards[5].description,
                label: copy.cards[5].label,
                backgroundMedia: (
                  <>
                    {cardBackdropByTheme("security")}
                    <SecurityTrustBackground />
                  </>
                ),
              },
            ]}
          />
        </div>
      </div>
    </section>
  );
};
